import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Box,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Quiz,
  Schedule,
  Assignment,
  Warning,
  Security,
  Wifi,
  AccessTime,
  Save,
  Visibility,
  VolumeOff,
  ShowChart,
} from '@mui/icons-material';
import { assessmentService } from '@/services/assessment/assessmentService';
import toast from 'react-hot-toast';

interface Assessment {
  _id: string;
  title: string;
  description: string;
  difficulty: string;
  isActive: boolean;
  configuration: {
    timeLimit: number;
    questionCount: number;
    passCriteria: number;
  };
}

interface AssessmentCardProps {
  assessment: Assessment;
  getDifficultyColor: (difficulty: string) => any;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({
  assessment,
  getDifficultyColor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [eligibilityError, setEligibilityError] = useState<string | null>(null);

  const handleTakeAssessment = async () => {
    setCheckingEligibility(true);
    setEligibilityError(null);
    
    try {
      const response = await assessmentService.checkEligibility(assessment._id);
      
      if (response.success && response.data?.eligible) {
        // User is eligible, navigate to exam
        setShowInstructions(false);
        navigate(`/exam/${assessment._id}`);
      } else {
        // User is not eligible, show error
        const reason = response.data?.reason || 'You are not eligible to take this assessment at this time.';
        setEligibilityError(reason);
              toast.error(reason);

      }
    } catch (error: any) {
      console.error('Eligibility check failed:', error);
      setEligibilityError(error.message || 'Failed to check eligibility. Please try again.');
      toast.error(error.message || 'Failed to check eligibility. Please try again.')
    } finally {
      setCheckingEligibility(false);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        width:'100%',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Quiz sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {assessment.title}
          </Typography>
          <Chip
            label={assessment.isActive ? 'Active' : 'Inactive'}
            color={assessment.isActive ? 'success' : 'default'}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
          {assessment.description}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Schedule sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Duration: {assessment.configuration.timeLimit} minutes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Assignment sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Questions: {assessment.configuration.questionCount}
            </Typography>
          </Box>
             <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt:1 }}>
            <ShowChart sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
  {/* {Math.floor(Math.random() * 1000) + 1} people attended this */}
  900 people attended this
</Typography>
</Box>
          <Typography variant="caption" color="text.secondary">
            Pass Criteria: {assessment.configuration.passCriteria}%
          </Typography>
        
        </Box>

        <Box sx={{ mb: 2 }}>
          <Chip
            label={assessment.difficulty}
            color={getDifficultyColor(assessment.difficulty)}
            size="small"
            variant="outlined"
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={() => setShowInstructions(true)}
          disabled={!assessment.isActive}
          sx={{ mt: 'auto', textTransform: 'none', borderRadius: 2 }}
        >
          Start Assessment
        </Button>
      </CardContent>

      <Dialog
        open={showInstructions}
        onClose={() => {
          setShowInstructions(false);
          setEligibilityError(null);
        }}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
            }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Assessment Instructions & Security Notice
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please read all instructions carefully before proceeding. This assessment is monitored for integrity purposes.
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {eligibilityError && (
            <Alert 
              severity="error" 
              icon={<Warning />}
              sx={{ mb: 3 }}
              onClose={() => setEligibilityError(null)}
            >
              {eligibilityError}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {assessment.title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {assessment.description}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Assessment Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Duration:</strong> {assessment.configuration.timeLimit} minutes
              </Typography>
              <Typography variant="body2">
                <strong>Questions:</strong> {assessment.configuration.questionCount}
              </Typography>
              <Typography variant="body2">
                <strong>Pass Criteria:</strong> {assessment.configuration.passCriteria}%
              </Typography>
              <Typography variant="body2">
                <strong>Difficulty:</strong> {assessment.difficulty}
              </Typography>
            </Box>
          </Box>

          {/* Pre-Assessment Requirements */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
              Pre-Assessment Requirements
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Wifi color="primary" />
                <Typography variant="body2">
                  <strong>Stable Internet Connection:</strong> Ensure you have a reliable internet connection. Assessment cannot be paused if connection is lost.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <VolumeOff color="primary" />
                <Typography variant="body2">
                  <strong>Quiet Environment:</strong> Choose a distraction-free location where you can focus completely on the assessment.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Assessment Rules */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
              Assessment Rules & Guidelines
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AccessTime color="warning" />
                <Typography variant="body2">
                  <strong>Time Management:</strong> You have {assessment.configuration.timeLimit} minutes to complete {assessment.configuration.questionCount} questions. The timer cannot be paused once started.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Save color="warning" />
                <Typography variant="body2">
                  <strong>Auto-Save:</strong> Your answers are automatically saved as you progress. You can navigate between questions freely.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Assignment color="warning" />
                <Typography variant="body2">
                  <strong>Completion:</strong> You must achieve {assessment.configuration.passCriteria}% to pass. Submit before time expires or it will auto-submit.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Security & Monitoring */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
              Security & Monitoring Notice
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                This assessment is monitored for security purposes
              </Typography>
            </Alert>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Security color="error" />
                <Typography variant="body2">
                  <strong>Browser Monitoring:</strong> Do not close, refresh, or navigate away from this tab. Such actions will be logged and may result in automatic submission.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Visibility color="error" />
                <Typography variant="body2">
                  <strong>Focus Tracking:</strong> Switching to other applications, opening new tabs, or losing window focus will be monitored and logged.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Warning color="error" />
                <Typography variant="body2">
                  <strong>Academic Integrity:</strong> Any suspicious activity during the assessment will be recorded and may result in disqualification.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Final Checklist */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
              Before You Begin - Final Checklist
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✓ I have a stable internet connection and backup plan if needed
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✓ I am in a quiet, distraction-free environment
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✓ I will not close, refresh, or navigate away from the assessment tab
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                ✓ I understand that all activity is monitored for security purposes
              </Typography>
              <Typography component="li" variant="body2">
                ✓ I am ready to complete the assessment within the time limit
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setShowInstructions(false);
              setEligibilityError(null);
            }}
            variant="outlined"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTakeAssessment}
            variant="contained"
            disabled={checkingEligibility}
            startIcon={checkingEligibility ? <CircularProgress size={20} /> : <Security />}
            sx={{ 
              textTransform: 'none', 
              borderRadius: 2, 
              ml: 2, 
              px: 3,
              py: 1,
              fontWeight: 600 
            }}
          >
            {checkingEligibility ? 'Verifying Eligibility...' : 'Begin Monitored Assessment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};